import FindInPageIcon from '@mui/icons-material/FindInPage';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton } from '@mui/material';
import {
    GridCellParams,
    GridColDef,
    GridRenderCellParams,
    GridRowModel,
    GridRowModesModel,
    GridValueFormatterParams,
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExperimentConfig } from '../../../backendsdk';
import { TrackedDataGrid as StyledDataGrid } from '../../../components/TrackedComponents';
import useProfile from '../../../hooks/profile';
import { getDateTimeColumnType, localeObjectMap } from '../../../utils/DataGridDateTime';
import { CustomerConfigPageDefs } from '../../../utils/Pages';
import { timestampToDate } from '../../../utils/TimeFormatter';
import palette from '../../ColorPalette';
import { DEFAULT_TITLE } from '../../Layout';
import { gridLocalization } from '../OTA/MuiDeviceTable';
import ConfigModal from './ConfigModal';
import ExperimentDevicesComponent from './ExperimentDevices';
import QuickSearchToolbar from './QuickSearchToolbar';

interface ExperimentConfigComponentProps {
    experiments: ExperimentConfig[];
    loadExperiments: (controller?: AbortController) => void;
    toolbar: React.ReactNode;
}

const ExperimentConfigComponent: React.FC<ExperimentConfigComponentProps> = (props) => {
    const [showConfigRow, setShowConfigRow] = useState<number>();
    const [showDevicesRow, setShowDevicesRow] = useState<number>();
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [searchText, setSearchText] = useState<string>('');
    const { i18n, t } = useTranslation();
    const { profile } = useProfile();
    const apiRef = useGridApiRef();
    const locale = localeObjectMap[i18n.languages[0]];

    const rows = props.experiments.map((experiment) => {
        return {
            ...experiment,
            created_at: timestampToDate(experiment.created_at),
            expiration_date: experiment.expiration_date ? timestampToDate(experiment.expiration_date) : null,
        };
    });
    const filteredRows = rows.filter((row) => row.id.toString().includes(searchText) || row.name.includes(searchText));
    const rowIdtoRowNum = Object.fromEntries(rows.map((row, index) => [row.id, index]));

    useEffect(() => {
        document.title = `${DEFAULT_TITLE} | ${t(`navigator.${CustomerConfigPageDefs.name}`)}`;
    }, []);

    const renderOverlayCell = (params: GridRenderCellParams) => {
        return (
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                    id={`${params.id}-show-btn`}
                    data-testid={`${params.id}-show-btn`}
                    onClick={() => setShowConfigRow(params.row.id)}
                    color="primary"
                >
                    <FindInPageIcon />
                </IconButton>
            </Box>
        );
    };

    const columns: GridColDef[] = useMemo(() => {
        const columns: GridColDef[] = [
            {
                field: 'id',
                type: 'string',
                headerName: 'id',
                headerAlign: 'left',
                align: 'left',
                width: 100,
                editable: false,
                hideable: true,
            },
            {
                field: 'name',
                type: 'string',
                headerName: 'name',
                headerAlign: 'left',
                align: 'left',
                width: 250,
                editable: false,
                hideable: true,
            },
            {
                field: 'maximum_vehicles',
                type: 'number',
                headerName: 'maximum_vehicles',
                headerAlign: 'left',
                align: 'left',
                width: 150,
                editable: false,
                hideable: true,
            },
            {
                field: 'device_count',
                type: 'number',
                headerName: 'device_count',
                headerAlign: 'left',
                align: 'left',
                width: 150,
                editable: false,
                hideable: true,
                renderCell: (params: GridRenderCellParams) => (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        {params.value}
                        <IconButton
                            id={`${params.id}-show-btn`}
                            data-testid={`${params.id}-show-btn`}
                            onClick={() => setShowDevicesRow(params.row.id)}
                            color="primary"
                        >
                            <OpenInNewIcon />
                        </IconButton>
                    </Box>
                ),
            },
            {
                field: 'created_at',
                type: 'number',
                headerName: 'created_at',
                headerAlign: 'left',
                align: 'left',
                ...getDateTimeColumnType(locale),
                width: 200,
                valueFormatter: (params: GridValueFormatterParams) =>
                    !!params.value ? dayjs(params.value).format(profile.dateTimeFormat) : '',
                editable: false,
                hideable: true,
            },
            {
                field: 'expiration_date',
                type: 'number',
                headerName: 'expiration_date',
                headerAlign: 'left',
                align: 'left',
                ...getDateTimeColumnType(locale),
                width: 200,
                valueFormatter: (params: GridValueFormatterParams) =>
                    !!params.value ? dayjs(params.value).format(profile.dateTimeFormat) : 'N/A',
                editable: false,
                hideable: true,
            },
            {
                field: 'overlay',
                type: 'singleSelect',
                headerName: 'overlay',
                headerAlign: 'left',
                align: 'left',
                renderCell: renderOverlayCell,
                width: 100,
                editable: false,
                hideable: true,
            },
        ];
        return columns;
    }, [rows]);

    const selectedOverlay = props.experiments.find((experiment) => experiment.id === showConfigRow);
    const selectedDevices = props.experiments.find((experiment) => experiment.id === showDevicesRow);

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                pb: 2,
            }}
        >
            {!!selectedOverlay ? (
                <ConfigModal config={selectedOverlay.overlay} onClose={() => setShowConfigRow(undefined)} />
            ) : null}
            {!!selectedDevices ? (
                <ExperimentDevicesComponent
                    experiment={selectedDevices}
                    onClose={() => setShowDevicesRow(undefined)}
                    loadExperiments={props.loadExperiments}
                />
            ) : null}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                <StyledDataGrid
                    id="device-config-status-data-grid"
                    apiRef={apiRef}
                    density="compact"
                    columns={columns}
                    rows={filteredRows}
                    editMode="row"
                    onProcessRowUpdateError={() => null}
                    disableMultipleRowSelection
                    showCellVerticalBorder
                    showColumnVerticalBorder
                    disableRowSelectionOnClick
                    pagination={true}
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={setRowModesModel}
                    getCellClassName={(params: GridCellParams) => `${params.field}-${rowIdtoRowNum[params.row.id]}`}
                    getRowClassName={(params: GridRowModel) => `row-${params.id}`}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'status_updated_at', sort: 'desc' }],
                        },
                    }}
                    slots={{ toolbar: QuickSearchToolbar }}
                    slotProps={{
                        toolbar: {
                            value: searchText,
                            onChange: (event: React.ChangeEvent<HTMLInputElement>) => setSearchText(event.target.value),
                            refresh: props.loadExperiments,
                            children: props.toolbar,
                        },
                        pagination: { sx: { mr: '45px' } },
                        columnsPanel: {
                            sx: {
                                '& .MuiDataGrid-panelFooter button:first-child': { display: 'none' },
                            },
                        },
                    }}
                    localeText={
                        {
                            ...gridLocalization[i18n.languages[0]],
                            filterPanelRemoveAll: t('table.remove_all'),
                            columnMenuManageColumns: t('table.manage_columns'),
                            unpin: t('table.unpin'),
                            filterOperatorOnDate: t('table.on_date'),
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any
                    }
                    sx={{
                        height: '100%',
                        width: '100%',
                        '& .MuiDataGrid-columnHeaders': {
                            borderBottom: 'none',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                            borderBottom: `1px solid ${palette.neutral[400]}`,
                        },
                        '& .MuiDataGrid-row:hover': {
                            cursor: 'pointer',
                        },
                        '& .MuiDataGrid-cell--editing': {
                            padding: '0 !important',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        backgroundColor: palette.neutral[50],
                    }}
                />
            </LocalizationProvider>
        </Box>
    );
};

export default ExperimentConfigComponent;
